import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as product from "../../../services/product.services";
import CustomPagination from "../../../common/custompagination";
import Sorting from "../../../common/sorting";
import Breadcrums from "../../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { capitalizeFirstLetter, formateDateWithMonth, formateDateWithMonthAdmin, handleServerValidations, myToFixed, TrimText, usePermission } from "../../../../../utils/commonfunction";
import CustomRangepicker from "../../../common/rangepicker";
import { Placeholder, Tooltip, Whisper } from "rsuite";

const ProductsDetails = () => {
  const { can } = usePermission();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState("false");
  const [datalength, setDataLength] = useState("");
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingRef = React.useRef(false);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Products"), url: "" },
  ];
  const [resetdate, setResetDate] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);


  useEffect(() => {
    const getData = setTimeout(() => {

      if (search) {
        if (page === 1) setLoader(true);
        else setIsFetchingMore(true);

        const formData = new FormData();
        formData.append("page", page);
        formData.append("per_page", itemperpage);
        formData.append("sort", JSON.stringify(sorting));
        formData.append("search", JSON.stringify(search));
        formData.append("global_search", globalsearch);
        product.List(formData)
          .then((data) => {
            setDataLength(data.data.total_records);
            const newItems = data && data.data && data.data.list ? data.data.list : [];
            if (page === 1) {
              setList(newItems);
            } else {
              setList(prev => {
                const existingIds = new Set(prev.map(item => item._id));
                const uniqueNewItems = newItems.filter(item => !existingIds.has(item._id));
                return [...prev, ...uniqueNewItems];
              });
            }
            setLoader(false);
            setIsFetchingMore(false);
            isFetchingRef.current = false;
          })
          .catch((error) => {
            console.log("error ====> ", error);
            setLoader(false);
            setIsFetchingMore(false);
            isFetchingRef.current = false;
          });
      }

    }, 300);
    return () => clearTimeout(getData)

  }, [statsupdate, sorting, search, globalsearch, itemperpage, page]);

  useEffect(() => {
    setPage(1);
    setList([]);
    setSelectedIds([]);
  }, [sorting, search, globalsearch, statsupdate, location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 50 >=
        document.documentElement.scrollHeight
      ) {
        if (!loader && !isFetchingRef.current && list.length < datalength) {
           isFetchingRef.current = true;
           setIsFetchingMore(true);
           setPage(prev => prev + 1);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loader, isFetchingMore, list.length, datalength]);

  const isAllSelected = list.length > 0 && list.every((item) => selectedIds.includes(item._id));
  const isSomeSelected = list.some((item) => selectedIds.includes(item._id)) && !isAllSelected;

  const handleSelectAll = (checked) => {
    if (checked) {
      const currentPageIds = list.map((item) => item._id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    } else {
      const currentPageIds = new Set(list.map((item) => item._id));
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.has(id)));
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (!selectedIds.length) return;
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedIds.length} selected product(s)?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes_delete"),
    }).then((result) => {
      if (result.isConfirmed) {
        product
          .Delete({ ids: selectedIds })
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
              setSelectedIds([]);
              setStatusUpdate(!statsupdate);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.log("bulkDeleteError", error);
          });
      }
    });
  };

  function Deletefunction(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes_delete"),
    }).then((result) => {
      if (result.isConfirmed) {
        const idToDelete = data?._id || data;
        product.Delete(data).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            setSelectedIds((prev) => prev.filter((id) => id !== idToDelete));
            setStatusUpdate(!statsupdate);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
          .catch((error) => {
            console.log("deleteError");
          });
      }
    });
  }

  // sorting function start
  const handleSort = (e, column) => {
    setDefaultSorting(false);
    let sort = { order: 0, column: column };
    if (e.target.classList.contains("assc")) {
      sort.order = -1;
    } else {
      sort.order = 1;
    }
    setSorting(sort);
  };
  // sorting end

  // search or filter function
  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) {
      sr[key] = value;
    } else {
      delete sr[key];
    }
    setSearch(sr);
  };
  // search or filer end

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    prepareSearch();
    setSearch({});
    setResetDate(!resetdate);
    // $("#defaultstatus")[0].selectedIndex = 0;
  };

  const viewfunction = (row) => {
    navigate(`/admin/productManagement/products/view/${row._id}`);
  };

  const goToEdit = (row) => {
    navigate(`/admin/productManagement/products/edit/${row._id}`);
  };

  const ChangeStatus = (data, Num) => {
    const formData = new FormData();
    formData.append("_id", JSON.stringify(data).replaceAll('"', ""));
    formData.append("status", Num);
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#403fad",
      cancelButtonColor: "#f1388b",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        product
          .Status(formData)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
              setStatusUpdate(!statsupdate);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.log("error===>");
          });
      }
    });
  };



  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {/* table section */}
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">
                {t("Products")}
              </h6>
              <div className="d-flex align-items-center">
                {selectedIds.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    className="btn btn-danger float-right me-3 ripple d-flex align-items-center"
                    style={{ gap: "6px" }}
                  >
                    <i className="ri-delete-bin-6-line"></i>
                    <span>{t("Delete Selected")} ({selectedIds.length})</span>
                  </button>
                )}
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      placeholder={t("input_placeholder_search")}
                      onChange={(e) => {
                        setGlobalSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="me-3">
                  <CustomRangepicker
                    GetDateRange={(e) => {
                      prepareSearch("createdAt", e);
                    }}
                    resetdate={resetdate}
                  />
                </div>

                <button type="reset" value="Reset" onClick={resetFilter} className="btn btn-warning float-right mr-2" >
                  {t("btn_reset_filter")} </button>
              </div>
            </div>

            {
              loader ?
                <Placeholder.Paragraph className="w-100" rowHeight={30} rowSpacing={5} active={true} rows={7} />
                :
                <>
                  <div className="table-responsive min-h-400">
                    <table className="table table-bordered border-t0 table table-bordered card-table border-top text-nowrap w-100 text-nowrap w-100">
                      <thead>
                        <tr>
                          <th style={{ width: "40px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              className="cp"
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                              checked={isAllSelected}
                              ref={(input) => {
                                if (input) input.indeterminate = isSomeSelected;
                              }}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </th>
                          {/* SR NO. */}
                          <th className="position-relative select_head">
                            <span>{t("list_heading_sno")}</span>
                          </th>

                          <th>
                            <div className="rating_head">
                              <span>{t("list_heading_image")}</span>
                            </div>
                          </th>



                          {/* Name */}

                          <th>
                            <div className="rating_head">
                              <span>{t("list_heading_name")}</span>
                            </div>
                          </th>

                          <th>
                          <div className="rating_head">
                            <span>{t("list_heading_price")}</span>
                          </div>
                        
                        </th>
                          <th>
                          
                            <div className="rating_head">
                              <span>{t("list_heading_category")}</span>
                            </div>
                          </th>
                           {/* <th>
                          <div className="rating_head">
                            <span>{t("list_heading_price")}</span>
                          </div>
                        </th> */}

                          <th className="status_head">{t("list_heading_status")}</th>

                          <th className="created_head">
                            <div className="sorting_column">
                              <span>{t("list_heading_created_date")}</span>
                              <Sorting
                                sort={sorting}
                                handleSort={handleSort}
                                defaultSorting={defaultSorting}
                                column="createdAt"
                              />
                            </div>
                          </th>
                          <th className="action_head">{t("list_heading_action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <>
                          {list.length ? (
                            list.map((row, i) => {
                              return (
                                <tr key={i}>
                                  <td style={{ textAlign: "center" }}>
                                    <input
                                      type="checkbox"
                                      className="cp"
                                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                      checked={selectedIds.includes(row._id)}
                                      onChange={() => handleSelectRow(row._id)}
                                    />
                                  </td>
                                  <td>{row ? i + 1 : null}</td>
                                  <td>
                                    {row?.image ? (
                                      <img
                                        src={row.image}
                                        alt={row.title}
                                        className="rounded border"
                                        style={{ width: "45px", height: "45px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div className="bg-light rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "45px", height: "45px", fontSize: "10px" }}>
                                        N/A
                                      </div>
                                    )}
                                  </td>

                                  {/* Question  */}

                                  <td>{row?.title ? <div className="d-flex">{row?.title ? capitalizeFirstLetter(TrimText(row.title, 50)) : "N/A"}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>{row?.price ? <div className="d-flex">{row?.price ? myToFixed(row.price) : "N/A"}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>{row?.category ? <div className="d-flex">{row?.category ? capitalizeFirstLetter(TrimText(row?.category, 50)) : "N/A"}</div> : <div className="d-flex">N/A</div>}</td>
                                  <td>
                                    <label className={"custom-switch cp" + (row.status !== 1 ? " e_none" : "")}>
                                      <input
                                        className="custom-switch-input cp"
                                        type="checkbox"
                                        checked={row?.status === 1}
                                        disabled={!can("/admin/productManagement/products/status") && !can("/admin/productManagement/products/edit")}
                                        onChange={(e) => (can("/admin/productManagement/products/status") || can("/admin/productManagement/products/edit")) && ChangeStatus(row?._id, row?.status === 1 ? 0 : 1)}
                                      />
                                      <span className="custom-switch-indicator cp"></span>
                                    </label>
                                  </td>

                                  {/* Answer */}

                                  <td>{row.createdAt ? formateDateWithMonthAdmin(row.createdAt) : "N/A"}</td>

                                  <td>
                                    <div className="btn-toolbar table-actions" role="toolbar" aria-label="Toolbar with button groups">
                                      <div className="btn-group btn-group-sm" role="group" aria-label="Second group">
                                         <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>View</Tooltip>}>
                                       <button
                                         type="button"
                                         className="btn view"
                                         onClick={() => {
                                           viewfunction(row);
                                         }}>
                                         <i className="ri-eye-fill"></i>
                                       </button>
                                     </Whisper>
                                        {can("/admin/productManagement/products/edit") && (
                                          <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}>
                                            <button type="button" className="btn edit" onClick={() => { goToEdit(row); }}>
                                              <i className="ri-pencil-fill"></i>
                                            </button>
                                          </Whisper>
                                        )}
                                        {can("/admin/productManagement/products/delete") && (
                                          <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}>
                                            <button type="button" className="btn delete" onClick={() => { Deletefunction(row) }}>
                                              <i className="ri-delete-bin-6-fill"></i>
                                            </button>
                                          </Whisper>
                                        )}
                                      </div>
                                    </div>

                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={11} className="text-center">
                                {t("msg_no_records")}
                              </td>
                            </tr>
                          )}
                        </>
                      </tbody>
                    </table>
                  </div>
                  <div className="" id="example1_info" role="status" aria-live="polite" >
                    <b> {t("msg_total_records")} : {datalength ? datalength : "0"} </b>
                  </div>
                  {isFetchingMore && (
                    <div className="text-center mt-3 mb-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </>
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductsDetails;
